/**
 * Per-type handler functions for TableService.manageColumnFilters.
 * Each handler receives (service, columnKey, value, columnProps) and mutates
 * the service's filter state by calling its add/remove methods.
 *
 * COLUMN_FILTER_HANDLERS maps each FILTER_TYPE_* constant to its handler so
 * TableService can perform an O(1) lookup instead of a switch statement.
 */
import moment from "../../moment";
import config from "../../config";
import {
  FILTER_TYPE_DATE_RANGE,
  FILTER_TYPE_SELECT_MODE_1,
  FILTER_TYPE_SELECT_MODE_2,
  FILTER_TYPE_SELECT_MODE_3,
  FILTER_TYPE_STATIC_SELECT,
  FILTER_TYPE_STRING
} from "./methods";

/** Returns true for any value that should be treated as "set", including explicit 0. */
const hasValue = (v) => v || v === 0;

/**
 * FILTER_TYPE_STRING → field/op=like/term filter.
 */
const handleStringFilter = (service, columnKey, value) => {
  service.removeFilter(columnKey);
  if (hasValue(value)) {
    service.addFilter({ column: columnKey, op: "like", value });
  }
};

/**
 * FILTER_TYPE_STATIC_SELECT / FILTER_TYPE_SELECT_MODE_2 → field/op=eq/term filter.
 * MODE_2 extracts the value from the first option object and spreads extra properties
 * (e.g. transKey) onto the filter.
 */
const handleSelectFilter = (service, columnKey, value, columnProps) => {
  const isMode2 = columnProps.filterType === FILTER_TYPE_SELECT_MODE_2;
  service.removeFilter(columnKey);
  if (hasValue(value)) {
    service.addFilter({
      column: columnKey,
      op: "eq",
      value: isMode2 ? value[0].value : value,
      ...(isMode2 ? value[0] : {})
    });
  }
};

/**
 * FILTER_TYPE_SELECT_MODE_1 / FILTER_TYPE_SELECT_MODE_3 → named "anotherFilter"
 * sent as `filters[nameKey]`. MODE_3 always wraps single values in an array to
 * support multi-value backend params.
 */
const handleAnotherFilter = (service, _columnKey, value, columnProps) => {
  const isMode3 = columnProps.filterType === FILTER_TYPE_SELECT_MODE_3;
  service.removeAnotherFilter(columnProps.filterNameKey);
  if (hasValue(value)) {
    const valueToSave = isMode3 ? { value: Array.isArray(value[0].value) ? value[0].value : [value[0].value] } : {};
    service.addAnotherFilter({ nameKey: columnProps.filterNameKey, ...value[0], ...valueToSave });
  }
};

/**
 * FILTER_TYPE_DATE_RANGE → two filters: lower bound (gte) and upper bound (lte),
 * formatted using the app's default date format.
 */
const handleDateRangeFilter = (service, columnKey, value) => {
  service.removeFilter(columnKey);
  if (value) {
    service.addFilter({ column: columnKey, op: "gte", value: moment(value[0]).format(config.defaultDateFormat) });
    service.addFilter({ column: columnKey, op: "lte", value: moment(value[1]).format(config.defaultDateFormat) });
  }
};

/**
 * Map from FILTER_TYPE_* constant → handler function.
 * Consumed by TableService.manageColumnFilters for O(1) dispatch.
 */
export const COLUMN_FILTER_HANDLERS = {
  [FILTER_TYPE_STRING]: handleStringFilter,
  [FILTER_TYPE_STATIC_SELECT]: handleSelectFilter,
  [FILTER_TYPE_SELECT_MODE_2]: handleSelectFilter,
  [FILTER_TYPE_SELECT_MODE_1]: handleAnotherFilter,
  [FILTER_TYPE_SELECT_MODE_3]: handleAnotherFilter,
  [FILTER_TYPE_DATE_RANGE]: handleDateRangeFilter
};
